import React, { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX, Repeat } from 'lucide-react';

const videoData = [
  {
    id: 1,
    url: "https://www.youtube.com/embed/0_npIUda64A",
    title: "Client Success Story – Kitter",
    muted: true,
    website: "https://codewithsadee.github.io/kitter/"
  },
  {
    id: 2,
    url: "https://www.youtube.com/embed/OC6G5rxkhXY",
    title: "Client Success Story – LearnKogit",
    muted: true,
    website: "https://learnkogit.com/"
  },
  {
    id: 3,
    url: "https://www.youtube.com/embed/rFeo04_BWA4",
    title: "Client Success Story – Ledger Tree",
    muted: true,
    website: "https://ledgertreefinancialgroup.com/"
  }
];

const VideoTestimonial = ({ excludeIds = [] }: { excludeIds?: number[] } = {}) => {
  const [videos, setVideos] = useState(videoData.filter(v => !excludeIds.includes(v.id)));
  const videoRefs = useRef([]);
  const iframeRefs = useRef([]);

  // Function to toggle mute state
  const toggleMute = (id) => {
    setVideos(prevVideos => {
      return prevVideos.map(video => {
        if (video.id === id) {
          const index = videos.findIndex(v => v.id === id);
          const iframe = iframeRefs.current[index];
          const newMutedState = !video.muted;
          if (iframe && iframe.src.includes('autoplay=1')) {
            iframe.src = `${video.url}?enablejsapi=1&autoplay=1&mute=${newMutedState ? 1 : 0}`;
          }
          return { ...video, muted: newMutedState };
        }
        return video;
      });
    });
  };

  // Function to replay video
  const replayVideo = (id) => {
    const index = videos.findIndex(v => v.id === id);
    const video = videos[index];
    const iframe = iframeRefs.current[index];
    if (iframe) {
      // Reset src to replay, keep mute/captions state
      iframe.src = `${video.url}?enablejsapi=1&autoplay=1&mute=${video.muted ? 1 : 0}&cc_load_policy=1`;
    }
  };

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const videoId = entry.target.getAttribute('data-video-id');
            const index = videos.findIndex(v => v.id.toString() === videoId);
            const iframe = iframeRefs.current[index];
            if (iframe && !iframe.src.includes('autoplay')) {
              const video = videos.find(v => v.id.toString() === videoId);
              if (video) {
                iframe.src = `${video.url}?enablejsapi=1&autoplay=1&mute=${video.muted ? 1 : 0}`;
              }
            }
          }
        });
      },
      { threshold: 0.1 }
    );
    videoRefs.current.forEach(ref => {
      if (ref) observer.observe(ref);
    });
    return () => {
      videoRefs.current.forEach(ref => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [videos]);

  return (
    <section>
      <h2 className="text-center text-3xl md:text-4xl font-bold mb-12 gradient-text">What Our Clients Say About Us</h2>
      <div className={`grid grid-cols-1 ${videos.length === 2 ? 'md:grid-cols-2 max-w-3xl' : 'md:grid-cols-3 max-w-6xl'} gap-6 mx-auto`}>
        {videos.map((video, index) => (
          <div
            key={video.id}
            ref={el => videoRefs.current[index] = el}
            data-video-id={video.id}
            className="relative w-full aspect-[9/16] rounded-[20px] overflow-hidden shadow-2xl hover:shadow-3xl transition-shadow duration-300 bg-gradient-to-br from-white via-gray-100 to-blue-100 dark:from-slate-900 dark:via-slate-800 dark:to-blue-950 border-4 border-primary/10 backdrop-blur-md"
          >
            <div className="relative h-full">
              <iframe
                ref={el => iframeRefs.current[index] = el}
                className="absolute top-0 left-0 w-full h-full scale-150"
                src={video.url}
                title={video.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
              <div className="absolute left-0 top-0 p-2 bg-primary/80 text-white text-xs rounded-br-lg z-10">{video.title}</div>
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
            {/* Mute toggle button */}
            <button
              onClick={() => toggleMute(video.id)}
              className="absolute bottom-4 right-4 bg-black bg-opacity-70 text-white p-2 rounded-full hover:bg-opacity-80 focus:outline-none transition-colors z-10"
              aria-label={video.muted ? "Unmute video" : "Mute video"}
            >
              {video.muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </button>
            {/* Replay button */}
            <button
              onClick={() => replayVideo(video.id)}
              className="absolute bottom-4 right-16 bg-black bg-opacity-70 text-white p-2 rounded-full hover:bg-opacity-80 focus:outline-none transition-colors z-10"
              aria-label="Replay video"
            >
              <Repeat size={20} />
            </button>
          </div>
        ))}
      </div>
      
      {/* Website links now shown below videos */}
      <div className={`grid grid-cols-1 ${videos.length === 2 ? 'md:grid-cols-2 max-w-3xl' : 'md:grid-cols-3 max-w-6xl'} gap-6 mx-auto mt-4`}>
        {videos.map((video, index) => (
          <div key={`link-${video.id}`} className="text-center">
            <a
              href={video.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-primary/90 text-white px-4 py-2 rounded-md transition hover:bg-primary/80 text-sm"
            >
              Visit Website: {video.title}
            </a>
          </div>
        ))}
      </div>
    </section>
  );
};

export default VideoTestimonial;
